import { booleanToBinary } from "@/helpers/common";
import {
  ANALYST,
  CLIENT_ANALYST,
  CLIENT_FUNDING_ADVISOR,
  CLIENT_UNDERWRITER,
  FUNDING_ADVISOR,
  UNDERWRITER,
} from "@/helpers/constants";
import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import { useAuth } from "@/hooks/auth";
import usersApiService from "@/services/modules/users";

import type { IPaginatedResponse } from "@/models/common";
import type { Role } from "@/models/options";
import type { IUser } from "@/models/users";
import type { GetUsersByRoleParams } from "@/services/modules/users";

type PaginationParams = Partial<
  Pick<GetUsersByRoleParams, "page" | "per_page" | "search">
>;

type GetPaginatedUsersCallback = (
  params: PaginationParams
) => Promise<IPaginatedResponse<IUser>>;

interface UseUsersForFiltersOptions {
  ignoreAssignedDeals?:
    | boolean
    | {
        analysts?: boolean;
        fundingAdvisors?: boolean;
        underwriters?: boolean;
        clientAnalysts?: boolean;
        clientFundingAdvisors?: boolean;
        clientUnderwriters?: boolean;
      };
}

export const useUsersForFilters = (options: UseUsersForFiltersOptions = {}) => {
  const { ignoreAssignedDeals = false } = options;
  const { currentUser } = useAuth();

  const getIgnoreAssignedDeals = (userType: string) => {
    if (typeof ignoreAssignedDeals === "boolean") {
      return ignoreAssignedDeals;
    }
    return (
      ignoreAssignedDeals[userType as keyof typeof ignoreAssignedDeals] ?? false
    );
  };

  const createCallback = (
    role: Role,
    userType: string,
    clientId?: string
  ): GetPaginatedUsersCallback => {
    return (params: PaginationParams) => {
      const serviceParams: GetUsersByRoleParams = {
        role,
        ...params,
      };
      const shouldIgnore = getIgnoreAssignedDeals(userType);
      if (shouldIgnore) {
        serviceParams.ignore_assigned_deals = booleanToBinary(shouldIgnore);
      }
      if (clientId) {
        serviceParams.client_id = clientId;
      }
      return usersApiService.getUsersByRole(serviceParams);
    };
  };

  const clientId = currentUser?.client_id;

  const createQueryKey = (role: Role, userType: string, cId?: string) =>
    QUERY_KEYS.USERS.byRole(role, {
      ignoreAssignedDeals: getIgnoreAssignedDeals(userType),
      ...(cId && { clientId: cId }),
    });

  return {
    getAnalysts: createCallback(ANALYST, "analysts"),
    getAnalystsQueryKey: createQueryKey(ANALYST, "analysts"),
    getFundingAdvisors: createCallback(FUNDING_ADVISOR, "fundingAdvisors"),
    getFundingAdvisorsQueryKey: createQueryKey(
      FUNDING_ADVISOR,
      "fundingAdvisors"
    ),
    getUnderwriters: createCallback(UNDERWRITER, "underwriters"),
    getUnderwritersQueryKey: createQueryKey(UNDERWRITER, "underwriters"),
    getClientAnalysts: createCallback(
      CLIENT_ANALYST,
      "clientAnalysts",
      clientId
    ),
    getClientAnalystsQueryKey: createQueryKey(
      CLIENT_ANALYST,
      "clientAnalysts",
      clientId
    ),
    getClientFundingAdvisors: createCallback(
      CLIENT_FUNDING_ADVISOR,
      "clientFundingAdvisors",
      clientId
    ),
    getClientFundingAdvisorsQueryKey: createQueryKey(
      CLIENT_FUNDING_ADVISOR,
      "clientFundingAdvisors",
      clientId
    ),
    getClientUnderwriters: createCallback(
      CLIENT_UNDERWRITER,
      "clientUnderwriters",
      clientId
    ),
    getClientUnderwritersQueryKey: createQueryKey(
      CLIENT_UNDERWRITER,
      "clientUnderwriters",
      clientId
    ),
  };
};

export type { GetPaginatedUsersCallback };
